import type { NextComponentType } from "next";

import ClientLayout from "./ClientLayout";
import { AppProps } from "next/app";

import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.min.css";
import "./index.css";
import React, { ReactNode, useEffect } from "react";
import { useRouter } from "next/router";
import { ErrorBoundary, initCspNonce } from "@bagel-web/common";
import { useParams } from "next/navigation";
import { SiteTitleContext } from "@bagel-web/components";

initCspNonce();

// Nested layouts are not supported by the NextJS Pages router.
// This is a workaround to allow pages to specify a secondary
// layout so that the wrapping layout isn't recreated on navigation.

type SecondaryLayoutComponentType = ({
  children,
}: {
  children: ReactNode;
}) => ReactNode;

type BagelWebComponentType = NextComponentType & {
  SecondaryLayoutComponent: SecondaryLayoutComponentType;
};

type BagelAppProps = AppProps & {
  Component: BagelWebComponentType;
};

const ConditionalSecondaryLayout = ({
  SecondaryLayoutComponent,
  children,
}: {
  SecondaryLayoutComponent?: SecondaryLayoutComponentType;
  children: ReactNode;
}) => {
  return SecondaryLayoutComponent ? (
    <SecondaryLayoutComponent>{children}</SecondaryLayoutComponent>
  ) : (
    children
  );
};

export default function RootLayout({ Component, pageProps }: BagelAppProps) {
  /* 
    This redirect is a hack to work with our static asset hosting.
    The web server directs all page requests to the top-level index.html,
    and we handle redirection to subpages via client-side routing.

    This is suboptimal for page performance, but is challenging to overcome
    with NextJS's current support for dynamic routes with static exports.
  */
  const router = useRouter();
  useEffect(() => {
    if (router.isReady && router.pathname !== "/_error") {
      router.push(router.asPath);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady]);

  const params = useParams();

  return (
    <>
      <div id="root">
        <SiteTitleContext.Provider value="Artificial Agency">
          <ClientLayout>
            <ErrorBoundary>
              {params && ( // Prevent rendering child components before params are loaded
                <ConditionalSecondaryLayout
                  SecondaryLayoutComponent={Component.SecondaryLayoutComponent}
                >
                  <Component {...pageProps} />
                </ConditionalSecondaryLayout>
              )}
            </ErrorBoundary>
          </ClientLayout>
        </SiteTitleContext.Provider>
      </div>
    </>
  );
}
